import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Dialog } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import defaultImage from '../../../assets/noImage.svg';
import { fetchAsSafeImageUrl } from '../../../utils/dataProcessing';

import {
  ImageSliderContainer,
  ImageCard,
  SpinnerContainer,
  StyledImage,
  StyledSlider,
  Title,
  StyledChip,
  SlideActionButton,
  BlockedSlideAction,
} from './styles';

const ImageSlider = ({
  resizedImageList,
  imageList,
  title,
  label,
  onImageAction,
  imageActionLabel,
  isActionBlocked,
  actionBlockedLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(false);
  // Which slide is showing. The index is the pose — attachment arrays are ordered identically across
  // check-ins, so a consumer can line this slide up with the same pose in another check-in.
  const [currentIndex, setCurrentIndex] = useState(0);
  const [height, setHeight] = useState(0);
  const [convertedUrls, setConvertedUrls] = useState({});
  const divRef = useRef(null);
  // Tracks indices that are already converting or done — avoids duplicate work
  const inProgressRef = useRef(new Set());
  // Tracks blob URLs we created so we can revoke them on cleanup
  const blobUrlsRef = useRef([]);

  const sourceList = resizedImageList.length ? resizedImageList : imageList;
  const sourceListRef = useRef(sourceList);
  useEffect(() => { sourceListRef.current = sourceList; });

  const convertImage = useCallback(async (index) => {
    if (inProgressRef.current.has(index)) return;
    inProgressRef.current.add(index);

    const url = sourceListRef.current[index];
    if (!url) {
      setConvertedUrls((prev) => ({ ...prev, [index]: '' }));
      return;
    }

    try {
      const { safeUrl, isBlob } = await fetchAsSafeImageUrl(url);
      if (isBlob) blobUrlsRef.current.push(safeUrl);
      setConvertedUrls((prev) => ({ ...prev, [index]: safeUrl }));
    } catch {
      setConvertedUrls((prev) => ({ ...prev, [index]: url }));
    }
  }, []);

  // Reset and convert first slide whenever imageList changes
  useEffect(() => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
    inProgressRef.current = new Set();
    setConvertedUrls({});
    // The slider snaps back to the first slide, so a stale index would report the wrong pose to
    // `onImageAction` — e.g. the side photo after paging to another check-in while on the front one.
    setCurrentIndex(0);

    if (imageList.length > 0) convertImage(0);
  }, [imageList, convertImage]);

  // Revoke all blob URLs on unmount
  useEffect(() => () => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const ASPECT_RATIO = 1.5;

  const handleImageClick = (image) => {
    setIsOpen(true);
    setCurrentImage(image);
  };

  useEffect(() => {
    const element = divRef.current;
    if (!element) return undefined;

    const updateHeight = () => {
      const { width: divWidth } = element.getBoundingClientRect();
      setHeight(divWidth / ASPECT_RATIO);
    };

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleSlideChange = useCallback((newIndex) => {
    setCurrentIndex(newIndex);
    convertImage(newIndex);
    if (newIndex + 1 < imageList.length) convertImage(newIndex + 1);
  }, [convertImage, imageList.length]);

  const getImageSlides = useCallback(() => {
    if (imageList.length === 0) {
      return <StyledImage src={defaultImage} />;
    }

    return imageList.map((imageUrl, index) => {
      const converted = convertedUrls[index];
      return (
        <ImageCard key={imageUrl}>
          {converted === undefined ? (
            <SpinnerContainer>
              <CircularProgress size={32} style={{ color: '#000000' }} />
            </SpinnerContainer>
          ) : (
            <StyledImage
              onClick={() => handleImageClick(converted)}
              src={converted}
              $height={height}
            />
          )}
          {label && <StyledChip variant="filled" label={label} />}
          <Dialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            onBackdropClick={() => setIsOpen(false)}
          >
            <StyledImage src={currentImage} />
          </Dialog>
        </ImageCard>
      );
    });
  }, [convertedUrls, imageList, isOpen, currentImage, label, height]);

  return (
    <ImageSliderContainer ref={divRef} $height={height}>
      {title && (
        <Title>{title}</Title>
      )}
      {/*
        One action for the slider, not one per slide: rendering it inside the slides put a button on
        every photo, stacked at the same spot, each reporting its own index regardless of which slide
        was visible. Anchored here it reports `currentIndex`, which is the pose actually on screen.
      */}
      {isActionBlocked && imageList.length > 0 && (
        <BlockedSlideAction
          type="button"
          aria-disabled
          title={actionBlockedLabel}
          aria-label={actionBlockedLabel}
        >
          <CloseOutlinedIcon fontSize="small" />
        </BlockedSlideAction>
      )}
      {!isActionBlocked && !!onImageAction && imageList.length > 0 && (
        <SlideActionButton
          type="button"
          onClick={() => onImageAction(currentIndex)}
          title={imageActionLabel}
          aria-label={imageActionLabel}
        >
          {/*
            A megaphone rather than a ⋯: clicking this starts creating a highlight immediately, so a
            menu affordance promised a menu that never appears. It matches the icon the mobile app
            already uses for a SOCIAL_MEDIA_POST coach task, which is what this photo becomes.
          */}
          <CampaignOutlinedIcon fontSize="small" />
        </SlideActionButton>
      )}
      <StyledSlider settings={{ afterChange: handleSlideChange }}>
        {getImageSlides()}
      </StyledSlider>
    </ImageSliderContainer>
  );
};

ImageSlider.propTypes = {
  imageList: PropTypes.arrayOf(PropTypes.string),
  resizedImageList: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  label: PropTypes.string,
  /**
   * Adds a per-slide action button, called with the slide index. The index is the pose — the
   * attachment arrays are ordered the same way across every check-in, so the caller can line this
   * slide up with the same pose in another check-in.
   */
  onImageAction: PropTypes.func,
  imageActionLabel: PropTypes.string,
  isActionBlocked: PropTypes.bool,
  actionBlockedLabel: PropTypes.string,
};

ImageSlider.defaultProps = {
  resizedImageList: [],
  imageList: [],
  title: '',
  label: '',
  onImageAction: null,
  imageActionLabel: '',
  isActionBlocked: false,
  actionBlockedLabel: '',
};

export default ImageSlider;
